/**
 * Amplitude `Identify` helper for Flagsmith feature flag user properties.
 *
 * Called from `FlagsmithAmplitudeSync` on OpenFeature `Ready` and
 * `ConfigurationChanged` events so that every Amplitude event in the session
 * can be sliced by flag cohort without those events needing to carry flag
 * context themselves.
 *
 * F1 scope: `isSaasTelemetryEnabled` is a build-time-folded `const boolean`,
 * so `if (!isSaasTelemetryEnabled) return;` lets DCE drop every Amplitude call
 * below — no `Identify`/`identify` ever executes on a suppressed build. See
 * .ai-context/onprem-guardrails.md (rule F1).
 */

import { shouldSyncFlagToAmplitude } from '@/providers/amplitude-flag-sync-policy';
import { isSaasTelemetryEnabled } from '@/utils/buildProfile';
import * as amplitude from '@amplitude/analytics-browser';

/**
 * Send all Flagsmith feature flags as Amplitude user properties.
 *
 * Each flag name is prefixed with `flag-` to avoid collisions with other user
 * properties. Flags with a remote value use that value; boolean-only flags use
 * their `enabled` state.
 *
 * @param flags - The full flag map from flagsmithClient.getAllFlags().
 *   Typed as a record by the SDK, but absent at runtime whenever the flag fetch
 *   never landed — a cold boot with no connectivity — so it is accepted as
 *   optional here rather than being let through to `Object.entries`.
 *
 *   Guarded at the funnel rather than at the caller. `openfeature-context-sync`
 *   already checks before calling, but that protects one call site: a second
 *   caller would reintroduce the same crash silently. Matches the PostHog
 *   transport, so both flag-sync paths are safe in the same place.
 */
export function setFlagsmithFlagUserProperties(
  flags: Record<string, { enabled: boolean; value?: unknown }> | undefined
): void {
  // TELEMETRY-GATE: F1 function-entry gate. Mirrors the canonical pattern in
  // src/providers/amplitude-provider.tsx — Amplitude must not be driven via
  // .identify() when telemetry is suppressed.
  if (!isSaasTelemetryEnabled) return;
  if (typeof window !== 'undefined' && window.__IS_E2E__) return;

  // Above the `try` deliberately: inside it, the resulting TypeError would be
  // caught below and mislabelled as "Amplitude unavailable".
  if (!flags) return;

  try {
    const identify = new amplitude.Identify();
    for (const [flagKey, flag] of Object.entries(flags)) {
      if (!shouldSyncFlagToAmplitude(flagKey)) continue;
      const flagValue =
        flag.value !== null && flag.value !== undefined ? flag.value : flag.enabled;
      identify.set(`flag-${flagKey}`, flagValue as string | number | boolean);
    }
    amplitude.identify(identify);
  } catch {
    // Amplitude unavailable (SSR, blocked by adblock, etc.) — non-fatal.
  }
}
